import { ApiError, api } from './api';

export type PushContext =
  | { kind: 'admin' }
  | { kind: 'player'; token: string };

interface Endpoints {
  vapid: string;
  subscribe: string;
  unsubscribe: string;
}

function endpoints(ctx: PushContext): Endpoints {
  if (ctx.kind === 'admin') {
    return {
      vapid: '/api/push/vapid-public-key',
      subscribe: '/api/push/subscribe',
      unsubscribe: '/api/push/unsubscribe',
    };
  }
  const base = `/api/players/${encodeURIComponent(ctx.token)}/push`;
  return {
    vapid: `${base}/vapid-public-key`,
    subscribe: `${base}/subscribe`,
    unsubscribe: `${base}/unsubscribe`,
  };
}

async function apiGet<T>(url: string): Promise<T> {
  const res = await fetch(url, { credentials: 'include' });
  if (!res.ok) throw new ApiError(res.status, await res.text());
  return res.json() as Promise<T>;
}
async function apiPost<T>(url: string, body: unknown): Promise<T> {
  const res = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new ApiError(res.status, await res.text());
  return res.json() as Promise<T>;
}

export type PushStatus =
  | 'unsupported'
  | 'denied'
  | 'not-configured'
  | 'idle'
  | 'subscribed';

function urlBase64ToUint8Array(base64: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(b64);
  const buf = new ArrayBuffer(raw.length);
  const out = new Uint8Array(buf);
  for (let i = 0; i < raw.length; i += 1) out[i] = raw.charCodeAt(i);
  return out;
}

function subToJson(sub: PushSubscription) {
  const json = sub.toJSON() as {
    endpoint?: string;
    keys?: { p256dh?: string; auth?: string };
  };
  if (!json.endpoint || !json.keys?.p256dh || !json.keys?.auth) {
    throw new Error('Invalid subscription');
  }
  return {
    endpoint: json.endpoint,
    keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
  };
}

export function isPushSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

export async function getPushStatus(): Promise<PushStatus> {
  if (!isPushSupported()) return 'unsupported';
  if (Notification.permission === 'denied') return 'denied';
  const reg = await navigator.serviceWorker.getRegistration();
  if (!reg) return 'idle';
  const sub = await reg.pushManager.getSubscription();
  return sub ? 'subscribed' : 'idle';
}

export async function enablePush(
  ctx: PushContext = { kind: 'admin' },
): Promise<PushStatus> {
  if (!isPushSupported()) return 'unsupported';
  const perm =
    Notification.permission === 'granted'
      ? 'granted'
      : await Notification.requestPermission();
  if (perm !== 'granted') return perm === 'denied' ? 'denied' : 'idle';

  const reg = await navigator.serviceWorker.ready;
  let sub = await reg.pushManager.getSubscription();
  const ep = endpoints(ctx);
  if (!sub) {
    const { publicKey } =
      ctx.kind === 'admin'
        ? await api.getPushVapidKey().catch(() => ({ publicKey: '' }))
        : await apiGet<{ publicKey: string }>(ep.vapid).catch(() => ({
            publicKey: '',
          }));
    if (!publicKey) return 'not-configured';
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey),
    });
  }
  const body = { ...subToJson(sub), ua: navigator.userAgent };
  if (ctx.kind === 'admin') {
    await api.pushSubscribe(body);
  } else {
    await apiPost(ep.subscribe, body);
  }
  return 'subscribed';
}

export async function disablePush(
  ctx: PushContext = { kind: 'admin' },
): Promise<PushStatus> {
  if (!isPushSupported()) return 'unsupported';
  const reg = await navigator.serviceWorker.getRegistration();
  if (!reg) return 'idle';
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return 'idle';
  const endpoint = sub.endpoint;
  try {
    await sub.unsubscribe();
  } catch {
    // ignore — server-side removal below still helps
  }
  const ep = endpoints(ctx);
  try {
    if (ctx.kind === 'admin') {
      await api.pushUnsubscribe(endpoint);
    } else {
      await apiPost(ep.unsubscribe, { endpoint });
    }
  } catch {
    // ignore
  }
  return 'idle';
}
